import { useMutation, useQuery, useQueryClient } from "react-query";
import { hasValue } from "../../helpers/nullable";
import { deleteAttachment, getAttachmentFolders, getAttachmentTags, getAttachmentsForCustomer, getAttachmentsInFolder, getPropertyPhotos, updateAttachment } from "./attachmentsApi";
import { notification } from "antd";
import { UpdateAttachmentData } from "./attachmentsModel";

export const useAttachmentFolders = (customerId: number, propertyId?: number, includeAll?: boolean) => useQuery(['attachments', 'folders', { customerId, propertyId, includeAll }], {
  queryFn: () => getAttachmentFolders(customerId, propertyId, includeAll),
  enabled: hasValue(customerId),
  refetchOnWindowFocus: false
})

export const useAttachmentsInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidateAllFolders: () => queryClient.invalidateQueries(['attachments', 'folders']),
    invalidateFolder: () => queryClient.invalidateQueries(['attachments', 'folder']),
    invalidateCustomer: () => queryClient.invalidateQueries(['attachments', 'customer'])
  }
}

export const useAttachmentsInFolder = (folder: string | undefined, customerId: number, propertyId?: number, jobId?: number) => {
  return useQuery(['attachments', 'folder', { folder, customerId, propertyId, jobId }],
    {
      queryFn: () => getAttachmentsInFolder(folder ?? '', customerId, propertyId, jobId),
      enabled: hasValue(customerId) && hasValue(folder),
      refetchOnWindowFocus: false
    }
  )
}

export const useCustomerAttachments = (customerId: number) => {
  return useQuery(['attachments', 'customer', customerId], {
    queryFn: () => getAttachmentsForCustomer(customerId),
    enabled: hasValue(customerId),
    refetchOnWindowFocus: false
  })
}

export const useAttachmentTags = (enabled: boolean) => useQuery(['attachments', 'tags'], {
  queryFn: getAttachmentTags,
  enabled,
})

export const useUpdateAttachmentMutation = (onSuccess: () => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateAttachmentData) => updateAttachment(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['attachments', 'folder'])
      onSuccess()
    },
    onError: () => { notification.error({ message: 'There was an error updating the attachment.' }) }
  })
}

export const useDeleteAttachmentMutation = (onSuccess: () => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (attachmentId: string) => deleteAttachment(attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries(['attachments', 'folder'])
      onSuccess()
    },
    onError: () => { notification.error({ message: 'There was an error deleting the attachment.' }) }
  })
}

export const usePropertyPhotosQuery = (customerId: number, propertyId: number) => {
  return useQuery(['attachments', 'property', 'photos', { customerId, propertyId }], {
    queryFn: () => getPropertyPhotos(customerId, propertyId),
    refetchOnWindowFocus: false
  })
}
